/*
Auto-generated by: https://github.com/pmndrs/gltfjsx
*/
import { animated, config, useSpringValue } from '@react-spring/three'
import { useGLTF } from '@react-three/drei'
import type { ObjectMap } from '@react-three/fiber'
import { Select } from '@react-three/postprocessing'
import { useEffect, useLayoutEffect, useMemo, useRef, type JSX } from 'react'
import { BufferGeometry, MathUtils, Matrix4, Mesh, MeshStandardMaterial, Object3D } from 'three'
import type { GLTF } from 'three-stdlib'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'

import { normalizeAngle, toLambert } from '@utils'

type GLTFResult = GLTF &
  ObjectMap & {
    nodes: {
      base: Mesh
      Cube: Mesh
      bol1: Mesh
      bolt2: Mesh
      bolt3: Mesh
      bolt4: Mesh
      pole1: Mesh
      middleCenter: Mesh
      middleTop: Mesh
      middleBottom: Mesh
      mesh106280964005: Mesh
      mesh106280964005_1: Mesh
      mesh106280964005_2: Mesh
      pole2: Mesh
      Circle: Mesh
      mesh411581995002: Mesh
      mesh411581995002_1: Mesh
      Chevron: Mesh
      Chevron001: Mesh
      Chevron002: Mesh
    }
    materials: {
      body: MeshStandardMaterial
      metal: MeshStandardMaterial
      details: MeshStandardMaterial
      black: MeshStandardMaterial
      red: MeshStandardMaterial
      yellow: MeshStandardMaterial
    }
  }

type SwitchIndicatorModelProps = JSX.IntrinsicElements['group'] & {
  targetRotation: number
  occupied?: boolean
  highlight?: boolean
}

function makeObj(
  pos?: [number, number, number],
  rot?: [number, number, number],
  scale?: number | [number, number, number],
) {
  const obj = new Object3D()
  if (pos) obj.position.set(...pos)
  if (rot) obj.rotation.set(...rot)
  if (scale !== undefined) {
    if (Array.isArray(scale)) {
      obj.scale.set(...scale)
    } else {
      obj.scale.setScalar(scale)
    }
  }
  obj.updateMatrix()
  return obj
}

// When matrix determinant < 0 (odd number of negative scale axes), applyMatrix4 flips
// the geometry but not the index winding — swap v1/v2 in each triangle to compensate.
function applyMatrix(geo: BufferGeometry, matrix: Matrix4): BufferGeometry {
  const result = geo.clone().applyMatrix4(matrix)
  if (matrix.determinant() < 0) {
    const index = result.index
    if (index) {
      for (let i = 0; i < index.count; i += 3) {
        const tmp = index.getX(i + 1)
        index.setX(i + 1, index.getX(i + 2))
        index.setX(i + 2, tmp)
      }
      index.needsUpdate = true
    }
  }
  return result
}

export function SwitchIndicatorModel({
  targetRotation,
  occupied,
  highlight = false,
  ...props
}: SwitchIndicatorModelProps) {
  const { nodes, materials: stdMaterials } = useGLTF('/models/railroad-switch.glb') as GLTFResult

  const materials = useMemo(
    () => ({
      body: toLambert(stdMaterials.body),
      metal: toLambert(stdMaterials.metal),
      details: toLambert(stdMaterials.details),
      black: toLambert(stdMaterials.black),
      red: toLambert(stdMaterials.red),
      yellow: toLambert(stdMaterials.yellow),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )
  const isMountedRef = useRef(false)
  const angleRef = useRef(targetRotation)

  const rotationY = useSpringValue(targetRotation, { config: { tension: 120, friction: 14 } })
  const rotationZ = useSpringValue(0, { config: config.stiff })

  useLayoutEffect(() => {
    const immediate = !isMountedRef.current
    isMountedRef.current = true
    const next = normalizeAngle(targetRotation, angleRef.current)
    angleRef.current = next
    rotationY.start(next, { immediate })

    if (!immediate) {
      rotationZ.start(MathUtils.degToRad(35), {
        onRest: () => rotationZ.start(0),
      })
    }
  }, [targetRotation, rotationY, rotationZ])

  const { staticGeometry, staticMaterials } = useMemo(() => {
    const lightGroup = makeObj([0, 1.025, 0], undefined, [1.372, 1.372, 0.8])

    const parts = [
      // {
      //   geo: applyMatrix(
      //     nodes.base.geometry,
      //     makeObj([0, 0.015, 0], undefined, [0.113, 0.015, 0.113]).matrix,
      //   ),
      //   mat: materials.body,
      // },
      {
        geo: applyMatrix(
          nodes.Cube.geometry,
          makeObj([0, 0.044, 0], undefined, [-0.063, -0.015, -0.063]).matrix,
        ),
        mat: materials.metal,
      },
      {
        geo: applyMatrix(
          nodes.bol1.geometry,
          makeObj([-0.043, 0.054, -0.045], [0, -0.742, 0], -0.01).matrix,
        ),
        mat: materials.details,
      },
      {
        geo: applyMatrix(
          nodes.bolt2.geometry,
          makeObj([0.041, 0.054, -0.045], [0, -1.105, 0], -0.01).matrix,
        ),
        mat: materials.details,
      },
      {
        geo: applyMatrix(
          nodes.bolt3.geometry,
          makeObj([0.041, 0.054, 0.044], [0, 0.911, 0], -0.01).matrix,
        ),
        mat: materials.details,
      },
      {
        geo: applyMatrix(
          nodes.bolt4.geometry,
          makeObj([-0.044, 0.054, 0.044], [Math.PI, -1.459, Math.PI], -0.01).matrix,
        ),
        mat: materials.details,
      },
      {
        geo: applyMatrix(
          nodes.pole1.geometry,
          makeObj([0, 0.314, 0.001], [-Math.PI, 0, -Math.PI], [-0.03, -0.26, -0.03]).matrix,
        ),
        mat: materials.body,
      },
      {
        geo: applyMatrix(
          nodes.middleCenter.geometry,
          makeObj([0, 0.577, 0], undefined, [-0.04, -0.049, -0.04]).matrix,
        ),
        mat: materials.body,
      },
      {
        geo: applyMatrix(
          nodes.middleTop.geometry,
          makeObj([0, 0.595, 0], undefined, -0.031).matrix,
        ),
        mat: materials.body,
      },
      {
        geo: applyMatrix(
          nodes.middleBottom.geometry,
          makeObj([0, 0.562, 0], undefined, -0.031).matrix,
        ),
        mat: materials.body,
      },
      {
        geo: applyMatrix(nodes.mesh106280964005.geometry, lightGroup.matrix),
        mat: materials.body,
      },
      {
        geo: applyMatrix(nodes.mesh106280964005_1.geometry, lightGroup.matrix),
        mat: materials.black,
      },
      {
        geo: applyMatrix(
          nodes.pole2.geometry,
          makeObj([0, 0.81, 0], [-Math.PI, 0, -Math.PI], [-0.01, -0.223, -0.01]).matrix,
        ),
        mat: materials.black,
      },
      {
        geo: applyMatrix(nodes.Circle.geometry, makeObj([0, 0.602, 0], undefined, 0.031).matrix),
        mat: materials.body,
      },
    ]

    const staticGeometry = mergeGeometries(
      parts.map(p => p.geo),
      true,
    )
    parts.forEach(p => p.geo.dispose())
    return { staticGeometry, staticMaterials: parts.map(p => p.mat) }
  }, [nodes, materials])

  const { leverGeometry, leverMaterials } = useMemo(() => {
    const subGroup = makeObj(undefined, [0.024, -0.02, 0.889], [0.799, 0.798, 0.471])

    const parts = [
      {
        geo: applyMatrix(nodes.mesh411581995002.geometry, subGroup.matrix),
        mat: materials.body,
      },
      {
        geo: applyMatrix(nodes.mesh411581995002_1.geometry, subGroup.matrix),
        mat: materials.details,
      },
    ]

    const leverGeometry = mergeGeometries(
      parts.map(p => p.geo),
      true,
    )
    parts.forEach(p => p.geo.dispose())
    return { leverGeometry, leverMaterials: parts.map(p => p.mat) }
  }, [nodes, materials])

  const { arrowGeometry, arrowMaterials } = useMemo(() => {
    const parts = [
      {
        geo: applyMatrix(
          nodes.Chevron.geometry,
          makeObj([0.131, 0.815, -0.001], undefined, [0.436, 0.773, 2.07]).matrix,
        ),
        mat: materials.yellow,
      },
      {
        geo: applyMatrix(
          nodes.Chevron001.geometry,
          makeObj([0, 0.815, -0.001], undefined, [0.436, 0.773, 2.07]).matrix,
        ),
        mat: materials.black,
      },
      {
        geo: applyMatrix(
          nodes.Chevron002.geometry,
          makeObj([-0.131, 0.815, -0.001], undefined, [0.436, 0.773, 2.07]).matrix,
        ),
        mat: materials.yellow,
      },
    ]

    const arrowGeometry = mergeGeometries(
      parts.map(p => p.geo),
      true,
    )
    parts.forEach(p => p.geo.dispose())
    return { arrowGeometry, arrowMaterials: parts.map(p => p.mat) }
  }, [nodes, materials])

  useEffect(() => {
    return () => {
      staticGeometry?.dispose()
      leverGeometry?.dispose()
      arrowGeometry?.dispose()
      Object.values(materials).forEach(m => m.dispose())
    }
  }, [staticGeometry, leverGeometry, arrowGeometry, materials])

  return (
    <group {...props}>
      <Select enabled={highlight}>
        <group position-y={-0.04}>
          <mesh castShadow receiveShadow geometry={staticGeometry} material={staticMaterials} />

          {/* Light */}
          <group position={[0, 1.025, 0]} scale={1.372} scale-z={0.8}>
            <mesh scale={[0.9, 0.9, 2]}>
              <icosahedronGeometry args={[0.044, 2]} />
              <meshLambertMaterial
                color={occupied ? 'red' : 'limegreen'}
                emissive={occupied ? 'red' : 'limegreen'}
                emissiveIntensity={10}
                toneMapped={false}
              />
              <pointLight color={occupied ? 'red' : 'limegreen'} intensity={1} />
            </mesh>
          </group>

          {/* Lever */}
          <animated.group position-y={0.111} rotation-z={rotationZ}>
            <mesh castShadow receiveShadow geometry={leverGeometry} material={leverMaterials} />
          </animated.group>

          {/* Arrow */}
          <animated.group rotation-y={rotationY} position-y={-0.05}>
            <mesh castShadow receiveShadow geometry={arrowGeometry} material={arrowMaterials} />
          </animated.group>
        </group>
      </Select>
    </group>
  )
}

useGLTF.preload('/models/railroad-switch.glb')
